import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { QRCodeCanvas } from 'qrcode.react';
import { database } from '../firebaseConfig';
import { ref, onValue } from 'firebase/database';

function Dashboard() {
  const [leaderboard, setLeaderboard] = useState([]);
  const qrValue = 'https://adopt.savemom.in/form';

  useEffect(() => {
    const leaderboardRef = ref(database, 'users');
    const unsubscribe = onValue(leaderboardRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const users = Object.entries(data).map(([id, user]) => ({
          id,
          ...user
        }));
        const sortedUsers = users.sort((a, b) => b.mothersAdapted - a.mothersAdapted);
        setLeaderboard(sortedUsers);
      }
    });

    return () => unsubscribe();
  }, []);

  const getTotalMothersAdapted = () => {
    return leaderboard.reduce((sum, user) => sum + (user.mothersAdapted || 0), 0);
  };

  return (
    <div className="App">
      <Helmet>
        <title>Savemom - Adopt a Mother Initiative | Help Expecting Mothers</title>
        <meta name="description" content="Join the Savemom Adopt a Mother Initiative. Help provide essential healthcare to expecting mothers. View our leaderboard of contributors making a difference." />
        <meta property="og:title" content="Savemom - Adopt a Mother Initiative" />
        <meta property="og:description" content="Join the Savemom Adopt a Mother Initiative. Help provide essential healthcare to expecting mothers." />
        <meta property="og:image" content="https://firebasestorage.googleapis.com/v0/b/savemom-starter.firebasestorage.app/o/systemimages%2Fdownload.png.1765516239066?alt=media&token=769e103e-9ce2-4c27-af5f-bff9b56bad9e" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Savemom - Adopt a Mother Initiative" />
        <meta name="twitter:description" content="Join the Savemom Adopt a Mother Initiative. Help provide essential healthcare to expecting mothers." />
        <meta name="twitter:image" content="https://firebasestorage.googleapis.com/v0/b/savemom-starter.firebasestorage.app/o/systemimages%2Fdownload.png.1765516239066?alt=media&token=769e103e-9ce2-4c27-af5f-bff9b56bad9e" />
        <link rel="icon" href="https://firebasestorage.googleapis.com/v0/b/savemom-starter.firebasestorage.app/o/systemimages%2Fdownload.png.1765516239066?alt=media&token=769e103e-9ce2-4c27-af5f-bff9b56bad9e" />
      </Helmet>
      {/* Header */}
      <header className="main-header">
        <div className="header-content">
          <div className="logo-section">
            <img 
              src="https://firebasestorage.googleapis.com/v0/b/savemom-starter.firebasestorage.app/o/systemimages%2Fdownload.png.1765516239066?alt=media&token=769e103e-9ce2-4c27-af5f-bff9b56bad9e" 
              alt="Savemom Logo" 
              className="logo-image"
            />
            <div className="logo-text">
              <h1>Savemom</h1>
              <span>Adopt a Mother Initiative</span>
            </div>
          </div>
          <div className="stats-summary">
            <div className="stat-item">
              <span className="stat-number">{leaderboard.length}</span>
              <span className="stat-label">Contributors</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">{getTotalMothersAdapted()}</span>
              <span className="stat-label">Mothers Adopted</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Dashboard */}
      <main className="dashboard">
        {/* Main Content Section - QR Code */}
        <section className="main-content-section">
          <div className="qr-display-section">
            <h2>Adopt a Mother</h2>
            <p>Scan the QR code below to fill out the registration form</p>
            
            <div className="qr-container">
              <QRCodeCanvas 
                value={qrValue}
                size={220}
                level="H"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#1f2937"
              />
            </div>
            
            <div className="qr-url">
              {qrValue}
            </div>
            
            <div className="qr-instructions">
              <h3>📱 How to Register</h3>
              <p>1. Open your phone camera and point it at the QR code</p>
              <p>2. Tap the notification that appears</p>
              <p>3. Fill out the form with your details</p>
              <p>4. Submit and see your name on the leaderboard!</p>
            </div>
          </div>
        </section>

        {/* Leaderboard Section */}
        <section className="leaderboard-section">
          <div className="section-header">
            <h2>🏆 Leaderboard</h2>
            <p>Our amazing contributors</p>
          </div>
          <div className="leaderboard-container">
            {leaderboard.length > 0 ? (
              <>
                {/* Top 3 Cards */}
                <div className="top-three">
                  {leaderboard.slice(0, 3).map((user, index) => (
                    <div key={user.id} className={`top-card rank-${index + 1}`}>
                      <div className="top-card-medal">
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                      </div>
                      <div className="top-card-avatar">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="top-card-info">
                        <span className="top-card-name"  style={{color:"black"}}>{user.name}</span>
                        <span className="top-card-count">{user.mothersAdapted} mothers</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Rest of the leaderboard as table */}
                {leaderboard.length > 3 && (
                  <div className="leaderboard-table">
                    <div className="table-header">
                      <span className="table-col rank-col">Rank</span>
                      <span className="table-col name-col">Name</span>
                      <span className="table-col count-col">Mothers</span>
                    </div>
                    <div className="table-body">
                      {leaderboard.slice(3).map((user, index) => (
                        <div key={user.id} className="table-row">
                          <span className="table-col rank-col">#{index + 4}</span>
                          <span className="table-col name-col">
                            <div className="user-avatar-small">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            {user.name}
                          </span>
                          <span className="table-col count-col">{user.mothersAdapted}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">🎯</div>
                <p>No contributors yet.</p>
                <p>Be the first to make a difference!</p>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="main-footer">
        <div className="footer-content">
          <p>Powered by <strong>Savemom</strong> 💚</p>
          <p className="footer-tagline">Together, we can save lives</p>
        </div>
      </footer>
    </div>
  );
}

export default Dashboard;
                        